import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, Copy, Activity, Download, Filter, CheckCircle2, AlertCircle, User } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllSessions } from '../api/sessions';
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
// --- Types ---
interface Session {
  id: string;
  startedAt: string;
  endedAt?: string;
  status: 'active' | 'completed' | 'incomplete';
  readingsCount: number;
  user?: {
    id: string;
    fullName: string;
    email: string;
  };
  device?: {
    id: string;
    deviceUid: string;
  };
}
// --- Main Component ---
export function Sessions() {
  const {
    token
  } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Modals & Panels State
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [toasts, setToasts] = useState<{
    message: string;
    type: 'success' | 'error';
  }[]>([]);
  // Live Timer State
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch Data
  const fetchSessions = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: searchQuery,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: currentPage,
        limit: 10
      };
      const res = await getAllSessions(token, filters);
      setSessions(res.sessions || []);
      setTotalSessions(res.total || 0);
      setTotalPages(Math.ceil(res.total / 10));
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [token, searchQuery, statusFilter, currentPage]);
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToasts(prev => [...prev, {
      message,
      type
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.message !== message));
    }, 3000);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor(diffMs % 3600000 / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-900/50 text-green-300 border-green-900/30';
      case 'in_progress':
        return 'bg-blue-900/50 text-blue-300 border-blue-900/30';
      case 'interrupted':
        return 'bg-red-900/50 text-red-300 border-red-900/30';
      default:
        return 'bg-gray-900/50 text-gray-300 border-gray-900/30';
    }
  };
  return <div className="h-full flex flex-col" data-id="element-854">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" data-id="element-855">
        <h1 className="text-2xl font-bold text-white" data-id="element-856">Sessions</h1>
        <button className="flex items-center justify-center gap-2 bg-[#6B21E8] hover:bg-[#5818c2] text-white px-4 py-2 rounded-lg transition-colors w-full sm:w-auto" data-id="element-857">
          <Download className="w-4 h-4" data-id="element-858" />
          Export CSV
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6" data-id="element-859">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto flex-1" data-id="element-860">
          <div className="relative w-full sm:w-auto" data-id="element-861">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-862" />
            <input type="text" placeholder="Search sessions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-[280px] bg-[#1A1A24] border border-gray-800 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors" data-id="element-863" />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-864">
            <Filter className="w-5 h-5 text-gray-400" data-id="element-865" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#1A1A24] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors w-full sm:w-auto" data-id="element-866">
              <option value="all" data-id="element-867">All Status</option>
              <option value="completed" data-id="element-868">Completed</option>
              <option value="in_progress" data-id="element-869">In Progress</option>
              <option value="interrupted" data-id="element-870">Interrupted</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-gray-400 hidden sm:block" data-id="element-871">
          Showing {(currentPage - 1) * 10 + 1}-
          {Math.min(currentPage * 10, totalSessions)} of {totalSessions}
        </div>
      </div>

      <div className="bg-[#1A1A24] border border-gray-800 rounded-xl flex-1 flex flex-col overflow-hidden" data-id="element-872">
        <div className="overflow-x-auto flex-1" data-id="element-873">
          <table className="w-full text-sm" data-id="element-874">
            <thead data-id="element-875">
              <tr className="border-b border-gray-800" data-id="element-876">
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-877">
                  Session
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-878">
                  User
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-879">
                  Device UID
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-880">
                  Status
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-881">
                  Duration
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-882">
                  Readings
                </th>
                <th className="text-right py-4 px-4 font-medium text-gray-400" data-id="element-883">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-884">
              {isLoading ? <tr data-id="element-885">
                  <td colSpan={7} className="text-center py-8 text-gray-400" data-id="element-886">
                    <div className="flex items-center justify-center gap-2" data-id="element-887">
                      <div className="w-5 h-5 border-2 border-[#6B21E8] border-t-transparent rounded-full animate-spin" data-id="element-888" />
                      Loading sessions...
                    </div>
                  </td>
                </tr> : sessions.length === 0 ? <tr data-id="element-889">
                  <td colSpan={7} className="text-center py-8 text-gray-400" data-id="element-890">
                    No sessions found matching your criteria.
                  </td>
                </tr> : sessions.map(session => <tr key={session.id} className="border-b border-gray-800 hover:bg-gray-900/50 transition-colors" data-id="element-891">
                    <td className="py-4 px-4" data-id="element-892">
                      <div className="flex items-center gap-3" data-id="element-893">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center" data-id="element-894">
                          <User className="w-4 h-4 text-gray-400" data-id="element-895" />
                        </div>
                        <div data-id="element-896">
                          <div className="font-medium text-white" data-id="element-897">
                            {session.userName}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-898">
                            {session.userEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-300 font-mono text-xs hidden md:table-cell" data-id="element-899">
                      {session.deviceUid}
                    </td>
                    <td className="py-4 px-4" data-id="element-900">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`} data-id="element-901">
                        {session.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-902" />}
                        {session.status === 'in_progress' && <Activity className="w-3.5 h-3.5" data-id="element-903" />}
                        {session.status === 'interrupted' && <AlertCircle className="w-3.5 h-3.5" data-id="element-904" />}
                        {session.status.replace('_', ' ').charAt(0).toUpperCase() + session.status.slice(1).replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-300" data-id="element-905">
                      {formatDuration(session.durationSeconds)}
                    </td>
                    <td className="py-4 px-4 text-gray-300 hidden md:table-cell" data-id="element-906">
                      {session.readingsCount}
                    </td>
                    <td className="py-4 px-4 text-right" data-id="element-907">
                      <button onClick={() => setSelectedSession(session)} className="text-gray-400 hover:text-white transition-colors" data-id="element-908">
                        <Eye className="w-4 h-4" data-id="element-909" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-800" data-id="element-910">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400" data-id="element-911">
            <div className="sm:hidden" data-id="element-912">
              Showing {(currentPage - 1) * 10 + 1}-
              {Math.min(currentPage * 10, totalSessions)} of {totalSessions}
            </div>
            <div className="flex items-center gap-2" data-id="element-913">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-914">
                <ChevronLeft className="w-5 h-5" data-id="element-915" />
              </button>
              <span className="text-white font-medium" data-id="element-916">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-917">
                <ChevronRight className="w-5 h-5" data-id="element-918" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedSession && <div className="fixed inset-y-0 right-0 z-50 flex" data-id="element-919">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedSession(null)} data-id="element-920" />
          <div className="relative w-full max-w-[460px] h-full bg-[#1A1A24] border-l border-gray-800 flex flex-col shadow-2xl ml-auto" data-id="element-921">
            <div className="flex items-center justify-between p-6 border-b border-gray-800" data-id="element-922">
              <h2 className="text-xl font-bold text-white" data-id="element-923">Session Details</h2>
              <button onClick={() => setSelectedSession(null)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" data-id="element-924">
                <X className="w-5 h-5" data-id="element-925" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-926">
              <div className="bg-gray-900/50 rounded-lg p-4" data-id="element-927">
                <h3 className="text-sm font-medium text-gray-300 mb-2" data-id="element-928">
                  Session Information
                </h3>
                <div className="space-y-2 text-xs" data-id="element-929">
                  <div className="flex justify-between" data-id="element-930">
                    <span className="text-gray-400" data-id="element-931">Session ID:</span>
                    <span className="text-white" data-id="element-932">{selectedSession.id}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-933">
                    <span className="text-gray-400" data-id="element-934">User:</span>
                    <span className="text-white" data-id="element-935">
                      {selectedSession.userName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-936">
                    <span className="text-gray-400" data-id="element-937">Device UID:</span>
                    <span className="text-white" data-id="element-938">
                      {selectedSession.deviceUid}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-939">
                    <span className="text-gray-400" data-id="element-940">Status:</span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedSession.status)}`} data-id="element-941">
                      {selectedSession.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-942" />}
                      {selectedSession.status === 'in_progress' && <Activity className="w-3.5 h-3.5" data-id="element-943" />}
                      {selectedSession.status === 'interrupted' && <AlertCircle className="w-3.5 h-3.5" data-id="element-944" />}
                      {selectedSession.status.replace('_', ' ').charAt(0).toUpperCase() + selectedSession.status.slice(1).replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-945">
                    <span className="text-gray-400" data-id="element-946">Duration:</span>
                    <span className="text-white" data-id="element-947">
                      {formatDuration(selectedSession.durationSeconds)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-948">
                    <span className="text-gray-400" data-id="element-949">Readings:</span>
                    <span className="text-white" data-id="element-950">
                      {selectedSession.readingsCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Toasts */}
      <div className="fixed top-20 right-4 sm:top-6 sm:right-6 z-50 flex flex-col gap-2" data-id="element-951">
        {toasts.map(toast => <div key={toast.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${toast.type === 'success' ? 'bg-green-900/80 text-green-100 border-green-900/50' : 'bg-red-900/80 text-red-100 border-red-900/50'}`} data-id="element-952">
            <div className={`w-2 h-2 rounded-full ${toast.type === 'success' ? 'bg-green-400' : 'bg-red-400'}`} data-id="element-953" />
            <span className="text-sm" data-id="element-954">{toast.message}</span>
          </div>)}
      </div>
    </div>;
}
// --- Subcomponents ---
function SessionDetailPanel({
  session,
  onClose,
  now
}: {
  session: Session | null;
  onClose: () => void;
  now: Date;
}) {
  if (!session) return null;
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor(diffMs % 3600000 / 60000);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-955">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-956" />
      <div className="relative w-[480px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-957">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-958">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-959">
            <X className="w-5 h-5" data-id="element-960" />
          </button>
          <div className="flex items-center gap-4" data-id="element-961">
            <div className="w-12 h-12 rounded-full bg-[rgba(59,130,246,0.1)] flex items-center justify-center flex-shrink-0" data-id="element-962">
              <Activity className="w-6 h-6 text-info" data-id="element-963" />
            </div>
            <div data-id="element-964">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-965">
                Session Detail
              </h2>
              <p className="text-[13px] text-text-secondary font-mono mt-1" data-id="element-966">
                {session.id}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-967">
          {/* BPM placeholder */}
          <div className="bg-background border border-border rounded-card p-6 text-center" data-id="element-968">
            <p className="text-[13px] text-text-tertiary" data-id="element-969">
              Detailed BPM chart available in patient app
            </p>
          </div>

          {/* Info */}
          <div className="space-y-4" data-id="element-970">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-971">
              Information
            </h3>
            <div className="space-y-3" data-id="element-972">
              <div className="flex justify-between items-center" data-id="element-973">
                <span className="text-[13px] text-text-secondary" data-id="element-974">Status</span>
                {session.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-975">
                    Active
                  </span>}
                {session.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-976">
                    Completed
                  </span>}
                {session.status === 'incomplete' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-977">
                    Incomplete
                  </span>}
              </div>
              <div className="flex justify-between" data-id="element-978">
                <span className="text-[13px] text-text-secondary" data-id="element-979">Started</span>
                <span className="text-[13px] text-white" data-id="element-980">
                  {new Date(session.startedAt).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-981">
                <span className="text-[13px] text-text-secondary" data-id="element-982">Ended</span>
                <span className="text-[13px] text-white" data-id="element-983">
                  {session.endedAt ? new Date(session.endedAt).toLocaleString() : '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-984">
                <span className="text-[13px] text-text-secondary" data-id="element-985">
                  Duration
                </span>
                <span className="text-[13px] text-white" data-id="element-986">
                  {formatDuration(session.startedAt, session.endedAt)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-987">
                <span className="text-[13px] text-text-secondary" data-id="element-988">
                  Total Readings
                </span>
                <span className="text-[13px] text-white" data-id="element-989">
                  {session.readingsCount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Entities */}
          <div className="space-y-4" data-id="element-990">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-991">
              Entities
            </h3>
            <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-992">
              <div data-id="element-993">
                <p className="text-[11px] text-text-secondary mb-1" data-id="element-994">User</p>
                <p className="text-[13px] text-white" data-id="element-995">
                  {session.user?.fullName || 'Unknown'}
                </p>
              </div>
              <button className="text-[12px] text-accent hover:underline" data-id="element-996">
                View
              </button>
            </div>
            <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-997">
              <div data-id="element-998">
                <p className="text-[11px] text-text-secondary mb-1" data-id="element-999">Device</p>
                <p className="text-[13px] text-white font-mono" data-id="element-1000">
                  {session.device?.deviceUid || 'Unknown'}
                </p>
              </div>
              <button className="text-[12px] text-accent hover:underline" data-id="element-1001">
                View
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}